'use client';

import { sanitizeEmailHtml } from '../../lib/email-html';
import { cn } from '../../lib/utils';

export interface MessageBodyProps {
  /**
   * Retained sanitized HTML, when the workspace has it. Sanitized again here — a row
   * stored before the current sanitizer is only as clean as the one that wrote it.
   */
  html?: string | null;
  /** Plain text. The only thing rendered when there is no retained HTML. */
  text?: string | null;
  className?: string;
  /** Shown when neither representation is available. */
  emptyLabel?: string;
}

/**
 * The only sanctioned way to render an email message body.
 *
 * Reach for this instead of a hand-rolled `dangerouslySetInnerHTML`, for the same reason
 * `RichTextContent` exists — except the stakes are higher here, because this content was
 * written by whoever sent the email rather than by a user of the product. Provider HTML
 * must never reach the DOM without passing through `sanitizeEmailHtml`, and keeping the
 * only injection point in one component is what makes that checkable rather than hoped
 * for.
 *
 * Plain text wins when there is no retained HTML, which is the normal case for a
 * workspace that has not enrolled in retention. That is a real state, not a degraded one.
 */
export function MessageBody({
  html,
  text,
  className,
  emptyLabel = '(No content)',
}: MessageBodyProps) {
  if (html) {
    return (
      <div
        className={cn('clinic-message-body text-sm leading-relaxed text-foreground', className)}
        // Sanitized immediately above. This is the single injection point for provider
        // HTML in the product; adding another is what the sanitizer cannot protect you
        // from.
        dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(html) }}
      />
    );
  }

  if (text) {
    return (
      <p className={cn('whitespace-pre-wrap text-sm leading-relaxed text-foreground', className)}>
        {text}
      </p>
    );
  }

  return <p className={cn('text-sm italic text-muted-foreground', className)}>{emptyLabel}</p>;
}
